/*
 * The phone app's own small database (IndexedDB "crm-offline"), with a promise per call. Three stores:
 *   records  the last-viewed customers, vendors and invoices and the forms' lists, for offline use (offlineCache.ts)
 *   outbox   what was recorded while offline, sent later (outbox.ts)
 *   prefs    this device's own settings, kept across sign-outs: the organization of the tab used last, and when the
 *            install prompt was dismissed (orgCheck.ts, PwaUi.tsx). No personal data.
 * The session token is never stored here or anywhere on disk (src/api/client.ts holds it in memory).
 */

const NAME = "crm-offline";
const VERSION = 2;
export type StoreName = "records" | "outbox" | "prefs";

let opening: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") return Promise.reject(new Error("This browser has no offline storage."));
  opening ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("records")) db.createObjectStore("records", { keyPath: "key" });
      if (!db.objectStoreNames.contains("outbox")) db.createObjectStore("outbox", { keyPath: "id" });
      if (!db.objectStoreNames.contains("prefs")) db.createObjectStore("prefs", { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("Offline storage could not be opened."));
  }).catch((e: unknown) => {
    opening = null;
    throw e;
  });
  return opening;
}

const done = <T,>(req: IDBRequest<T>) =>
  new Promise<T>((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("Offline storage failed."));
  });

async function store(name: StoreName, mode: IDBTransactionMode) {
  return (await open()).transaction(name, mode).objectStore(name);
}

export const idb = {
  all: async <T,>(name: StoreName) => done((await store(name, "readonly")).getAll()) as Promise<T[]>,
  get: async <T,>(name: StoreName, key: string) => done((await store(name, "readonly")).get(key)) as Promise<T | undefined>,
  put: async <T,>(name: StoreName, value: T) => void (await done((await store(name, "readwrite")).put(value))),
  del: async (name: StoreName, key: string) => void (await done((await store(name, "readwrite")).delete(key))),
  clear: async (name: StoreName) => void (await done((await store(name, "readwrite")).clear())),
};
